/**
 * Remembering a "show more trains" tap across a reload.
 *
 * The deck is deliberately cut short — see `visibleTrains` in `profiles.ts` — but a rider who
 * asked for the 2pm train, then reloaded, must not lose it: on 2026-09-25 that was the exact
 * complaint. So the count survives a reload, but only for the view it was asked in. Reaching the
 * platform answers a different question than standing at school did, and the count for one must
 * not silently answer for the other — nor should this afternoon's asking still be in effect
 * tomorrow morning. The storage format lives here, away from React, so it can be tested.
 */

export interface StoredTrainWindow {
	date: string;
	/** The card question this count was asked under, e.g. "leave" or "platform". */
	view: string;
	/** How many "show more trains" taps this view has made today. */
	extra: number;
}

/** A count belongs to the day and the view it was asked in; anything else answers for neither. */
export function parseTrainWindow(raw: string | null, todayKey: string, view: string): number {
	if (!raw) {
		return 0;
	}
	try {
		const stored = JSON.parse(raw) as Partial<StoredTrainWindow>;
		if (stored?.date !== todayKey || stored.view !== view || typeof stored.extra !== "number") {
			return 0;
		}
		return Math.max(0, Math.trunc(stored.extra));
	} catch {
		return 0;
	}
}

export function serialiseTrainWindow(view: string, extra: number, todayKey: string): string {
	return JSON.stringify({ date: todayKey, view, extra } satisfies StoredTrainWindow);
}
